import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { formatPercentPoints, type StrainSummary } from '@shared';
import { StrainSelect } from './StrainSelect';
import { PrimaryButton, SecondaryButton } from './ui';
import { getProductRepository } from '../lib/productRepository';
import {
  addRitualAllocation,
  allocationPotency,
  buildRitualSaveValue,
  calculateRitualAllocation,
  defaultRitualTitle,
  formatRitualInput,
  formatRitualNumber,
  occurredAtIso,
  removeRitualAllocation,
  replaceRitualProduct,
  ritualTotals,
  switchRitualTargetMode,
  type RitualDraft,
  type RitualSaveValue,
} from '../lib/ritualModel';
import type { RitualTargetMode } from '../lib/journalStore';
import type { RitualPersistenceResult } from '../lib/ritualUnsavedChanges';

export type RitualFormValue = RitualSaveValue;

export interface RitualEntryFormHandle {
  submit(): boolean;
}

export function ritualDraftFingerprint(draft: RitualDraft): string {
  return JSON.stringify({
    title: draft.title,
    occurredAt: draft.occurredAt,
    targetMode: draft.targetMode,
    allocations: draft.allocations.map((row) => ({
      productId: row.product?.id ?? null,
      snapshot: row.snapshot ?? null,
      weightInput: row.weightInput,
      doseInput: row.doseInput,
    })),
    mood: draft.mood,
    effects: draft.effects,
    notes: draft.notes,
    blendLabel: draft.blendLabel,
  });
}

export function setRitualChangingRow(current: ReadonlySet<number>, index: number, changing: boolean): Set<number> {
  const next = new Set(current);
  if (changing) next.add(index); else next.delete(index);
  return next;
}

function ritualThumbnail(product: StrainSummary | null): string | null {
  return product?.primaryThumbnailUrl
    ?? product?.localPrimaryThumbnailUrl
    ?? product?.firstLocalThumbnailUrl
    ?? product?.spreadsheetThumbnailUrl
    ?? null;
}

export const RitualEntryForm = forwardRef<RitualEntryFormHandle, {
  initial: RitualDraft;
  submitLabel?: string;
  onSave: (value: RitualFormValue) => RitualPersistenceResult;
  onSaveSuccess: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}>(function RitualEntryForm({
  initial,
  submitLabel = 'Save Ritual',
  onSave,
  onSaveSuccess,
  onCancel,
  onDirtyChange,
}, ref): JSX.Element {
  const [draft, setDraft] = useState(initial);
  const baseline = useRef(ritualDraftFingerprint(initial));
  const [products, setProducts] = useState<StrainSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [changingRows, setChangingRows] = useState<Set<number>>(() => new Set());

  const dirty = ritualDraftFingerprint(draft) !== baseline.current;

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    let cancelled = false;
    getProductRepository().list({ archived: 'active', sort: 'name' })
      .then((rows) => { if (!cancelled) setProducts(rows); })
      .catch((error: unknown) => { if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Could not load catalogue products.'); });
    return () => { cancelled = true; };
  }, []);

  const multiple = draft.allocations.length > 1;
  const effectiveMode: RitualTargetMode = multiple ? 'weight' : draft.targetMode;
  const totals = useMemo(() => ritualTotals(draft.allocations, effectiveMode), [draft.allocations, effectiveMode]);
  const calculated = draft.allocations.map((row) => calculateRitualAllocation(row, effectiveMode, !multiple));
  const allocationlessLegacy = draft.allowAllocationlessLegacySave && draft.allocations.length === 0;
  const validConsumption = allocationlessLegacy || draft.allocations.every((row, index) =>
    Boolean(row.product || row.snapshot?.productName) && calculated[index]!.weightGrams > 0,
  );

  const updateRow = (index: number, patch: Partial<RitualDraft['allocations'][number]>): void => {
    setDraft((current) => ({ ...current, allocations: current.allocations.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row) }));
  };
  const addStrain = (): void => setDraft(addRitualAllocation);
  const removeStrain = (index: number): void => setDraft((current) => removeRitualAllocation(current, index));
  const setChanging = (index: number, changing: boolean): void =>
    setChangingRows((current) => setRitualChangingRow(current, index, changing));

  const submit = (): boolean => {
    const result = buildRitualSaveValue(draft);
    setDateError(result.error);
    if (!result.value) {
      setSubmitError(result.error ?? (!draft.title.trim() ? 'Enter a Ritual title.' : 'Select each strain and enter a positive Weight or Dose.'));
      return false;
    }
    const persistence = onSave(result.value);
    if (!persistence.saved) {
      setSubmitError(persistence.error ?? 'Could not save Ritual. Your changes are still unsaved.');
      return false;
    }
    baseline.current = ritualDraftFingerprint(draft);
    onDirtyChange?.(false);
    setSubmitError(null);
    onSaveSuccess();
    return true;
  };

  useImperativeHandle(ref, () => ({ submit }), [draft, onDirtyChange, onSave, onSaveSuccess]);

  return (
    <section className="card stack ritual-form" aria-label="Ritual Entry form">
      <div className="field" style={{ margin: 0 }}>
        <label htmlFor="ritual-title">Title</label>
        <input id="ritual-title" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} maxLength={120} placeholder="e.g. Evening wind-down" />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label htmlFor="ritual-occurred-at">Date &amp; Time</label>
        <input id="ritual-occurred-at" type="datetime-local" required value={draft.occurredAt} aria-invalid={dateError ? true : undefined} aria-describedby={dateError ? 'ritual-occurred-at-error' : undefined} onChange={(event) => { const value = event.target.value; setDraft({ ...draft, occurredAt: value }); setDateError(occurredAtIso(value) ? null : 'Enter a valid Date & Time.'); }} />
        {dateError ? <span id="ritual-occurred-at-error" className="notice" role="alert">{dateError}</span> : null}
      </div>

      {draft.allocations.length === 1 ? (
        <div className="filter-chips" role="group" aria-label="Target by Weight or Dose">
          {(['weight', 'dose'] as const).map((mode) => (
            <button key={mode} type="button" className={`filter-chip${draft.targetMode === mode ? ' filter-chip--active' : ''}`} aria-pressed={draft.targetMode === mode} onClick={() => setDraft((current) => switchRitualTargetMode(current, mode))}>
              {mode === 'weight' ? 'Weight' : 'Dose'}
            </button>
          ))}
        </div>
      ) : null}

      {draft.allocations.map((row, index) => {
        const result = calculated[index]!;
        const liveSnapshotProduct = row.snapshot?.productId
          ? products.find((product) => product.id === row.snapshot?.productId) ?? null
          : null;
        const displayProduct = row.product ?? liveSnapshotProduct;
        const thumbnailUrl = ritualThumbnail(displayProduct);
        const hasSelection = Boolean(row.product || row.snapshot?.productName);
        const changing = changingRows.has(index);
        return (
          <fieldset key={`${index}-${row.snapshot?.productId ?? 'new'}`} className="ritual-allocation stack">
            <legend>Strain {index + 1}</legend>
            {hasSelection ? (
              <div className="ritual-strain-card">
                {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="ritual-strain-card__thumb" /> : <span className="ritual-strain-card__thumb ritual-strain-card__placeholder" aria-hidden="true">🌿</span>}
                <div className="ritual-strain-card__details">
                  <strong>{row.product?.brand?.trim() || row.snapshot?.brand || 'Unknown brand'}</strong>
                  <span>{row.product?.productName?.trim() || row.product?.name?.trim() || row.snapshot?.productName}</span>
                  <span className="ritual-strain-card__potency">{formatPercentPoints(allocationPotency(row))} THC</span>
                </div>
              </div>
            ) : null}
            {!hasSelection ? <StrainSelect label={`Strain ${index + 1}`} strains={products} selectedId={null} excludeIds={draft.allocations.flatMap((other, otherIndex) => otherIndex === index ? [] : [other.product?.id ?? other.snapshot?.productId ?? ''])} onSelect={(product) => setDraft((current) => replaceRitualProduct(current, index, product))} /> : null}
            <div className="grid grid-2 ritual-allocation__values">
              <div className="field" style={{ margin: 0 }}>
                <label htmlFor={`ritual-weight-${index}`}>Weight</label>
                <div className="input-with-unit"><input id={`ritual-weight-${index}`} type="number" min="0" step="0.01" inputMode="decimal" value={effectiveMode === 'dose' && !multiple ? formatRitualNumber(result.weightGrams) : row.weightInput} readOnly={effectiveMode === 'dose' && !multiple} aria-describedby={`ritual-weight-help-${index}`} onChange={(event) => updateRow(index, { weightInput: event.target.value })} onBlur={() => updateRow(index, { weightInput: formatRitualInput(row.weightInput) })} /><span>g</span></div>
                <span id={`ritual-weight-help-${index}`} className="hint">{effectiveMode === 'dose' && !multiple ? 'Calculated from Dose' : 'You control this value'}</span>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label htmlFor={`ritual-dose-${index}`}>Dose</label>
                <div className="input-with-unit"><input id={`ritual-dose-${index}`} type="number" min="0" step="0.01" inputMode="decimal" value={effectiveMode === 'dose' && !multiple ? row.doseInput : formatRitualNumber(result.thcDoseMg)} readOnly={effectiveMode !== 'dose' || multiple} aria-describedby={`ritual-dose-help-${index}`} onChange={(event) => updateRow(index, { doseInput: event.target.value })} onBlur={() => updateRow(index, { doseInput: formatRitualInput(row.doseInput) })} /><span>mg THC</span></div>
                <span id={`ritual-dose-help-${index}`} className="hint">{effectiveMode === 'dose' && !multiple ? 'You control this value' : `Calculated at ${allocationPotency(row) ?? 'unknown'}% THC`}</span>
              </div>
            </div>
            {hasSelection || draft.allocations.length > 1 ? <div className="row ritual-allocation__actions">
              {hasSelection ? <button type="button" className="btn btn-secondary" onClick={() => setChanging(index, !changing)}>{changing ? 'Cancel' : 'Change Strain'}</button> : null}
              <button type="button" className="btn btn-ghost" disabled={draft.allocations.length <= 1} onClick={() => removeStrain(index)} aria-label={`Remove Strain ${index + 1}`}><X size={16} aria-hidden /> Remove Strain</button>
            </div> : null}
            {hasSelection && changing ? <StrainSelect label={`Choose replacement for Strain ${index + 1}`} strains={products} selectedId={null} excludeIds={draft.allocations.flatMap((other, otherIndex) => otherIndex === index ? [] : [other.product?.id ?? other.snapshot?.productId ?? ''])} onSelect={(product) => { setDraft((current) => replaceRitualProduct(current, index, product)); setChanging(index, false); }} /> : null}
          </fieldset>
        );
      })}

      {allocationlessLegacy ? <p className="hint">This migrated Ritual has no structured consumption details. You can save reflection edits as-is, or add a strain to intentionally create them.</p> : null}
      <button type="button" className="btn btn-secondary" onClick={addStrain}><Plus size={17} aria-hidden /> Add Strain</button>
      {multiple ? <dl className="ritual-totals"><div><dt>Total Weight</dt><dd>{formatRitualNumber(totals.totalWeightGrams)} g</dd></div><div><dt>Total Dose</dt><dd>{formatRitualNumber(totals.totalThcDoseMg)} mg THC</dd></div></dl> : null}
      {loadError ? <p className="notice" role="alert">{loadError}</p> : null}

      <div className="grid grid-2">
        <div className="field" style={{ margin: 0 }}><label htmlFor="ritual-mood">Mood</label><input id="ritual-mood" value={draft.mood} onChange={(event) => setDraft({ ...draft, mood: event.target.value })} maxLength={80} /></div>
        <div className="field" style={{ margin: 0 }}><label htmlFor="ritual-effects">Effects</label><input id="ritual-effects" value={draft.effects} onChange={(event) => setDraft({ ...draft, effects: event.target.value })} maxLength={120} /></div>
      </div>
      <div className="field" style={{ margin: 0 }}><label htmlFor="ritual-notes">Notes</label><textarea id="ritual-notes" value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} rows={4} maxLength={4000} /></div>
      {!validConsumption ? <p className="hint">Select each strain and enter a positive Weight or Dose.</p> : null}
      {submitError ? <p className="notice" role="alert">{submitError}</p> : null}
      <div className="row"><PrimaryButton onClick={() => submit()} disabled={!draft.title.trim() || !validConsumption || !occurredAtIso(draft.occurredAt)}>{submitLabel}</PrimaryButton><SecondaryButton onClick={onCancel}>Cancel</SecondaryButton></div>
      {!draft.title && defaultRitualTitle(draft.allocations) ? <button type="button" className="btn btn-ghost" onClick={() => setDraft({ ...draft, title: defaultRitualTitle(draft.allocations) })}>Use suggested title</button> : null}
    </section>
  );
});
